import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, FilePenLine, Route, Search, Square } from 'lucide-react'
import type { App as AppManifest, BlockDef, Page } from '@cogo/types-v2'
import type { ActionFlowEngine, PlatformAdapter } from '@cogo/runtime-v2'
import {
  type AppV2Props,
  type HostServices,
  type ModalComponentRegistry,
} from '@cogo/react-renderer-v2'
import type {
  ChannelOutput,
  PageProgress,
  ChannelMissReason,
} from '@/features/_shared/channel-types'
import { abortActiveChannelDispatch } from '@/features/channel-router'
import { useStudioStore } from '@/store/studio-store'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { findElementInTree } from '@/features/page-editor/store/canvas-slice'
import type { CogoElement } from '@/features/page-editor/store/types'
import { getMissReasonCopy } from './miss-reason-copy'
import { GenerationProvenanceCard } from './generation-provenance-card'
import {
  deriveGenerationProvenance,
  readSpecFieldProvenanceFromSettings,
  hasProvenanceContent,
  readIntakeDecisionsFromSettings,
} from '@/features/chat/services/generation-provenance'
import { measureRequirementRealization } from '@/features/chat/services/requirement-realization'
import { usePreviewTestingStore } from '@/features/preview/store/preview-testing-store'
import {
  collectRendererProjection,
  getPreviewEventHint,
  installActionFlowTraceCapture,
  readStudioTraceProjectBinding,
  type PreviewEventHint,
} from '@/features/preview/services/actionflow-trace'
import { buildPreviewRenderPackBytes } from '@/features/preview/services/render-pack-preview'
import { toRendererThemeTokens } from '@/features/preview/lib/renderer-theme-tokens'
import { STUDIO_PREVIEW_ISLANDS } from '@/features/preview/lib/preview-islands'
import {
  useStudioHostServices,
  type StudioHostServices,
} from '../host/use-studio-host-services'
import {
  createStudioRuntimePlatform,
  getStudioRuntimeApiBase,
  type StudioRuntimePlatformBundle,
} from '../host/studio-runtime-network'
import {
  createStudioDirectAppV2EffectBundle,
  materializeStudioHostCapabilityManifest,
  type StudioCropImageImplementation,
  type StudioHostCapabilityManifestInput,
} from '../host/studio-host-capability-manifest'
import { useAppDesignSandbox } from '@/features/preview/hooks/use-app-design-sandbox'
import { useProjectThumbnailAutoCapture } from '@/features/project/hooks/use-project-thumbnail-auto-capture'
import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'
import { showStudioRuntimeToast } from '../host/studio-runtime-toast'

type AppV2Component = ComponentType<AppV2Props>

type StudioHostCapabilityManifestEvidenceInput = Omit<
  StudioHostCapabilityManifestInput,
  'runtimePlatformBundle' | 'hostServicesBundle' | 'directAppV2EffectBundle'
>

interface BuildModeCanvasProps {
  routeSwitcherPortalElement?: HTMLElement | null
  onEditPage?: (pageId: string) => void
  hostCapabilityManifestInput?: StudioHostCapabilityManifestEvidenceInput
}

let cachedAppV2: AppV2Component | undefined
let cachedCropImageWeb: StudioCropImageImplementation | undefined

const CHIP_OUTLINE_HOLD_MS = 800
const CHIP_TOOLTIP_HOLD_MS = 1200

/**
 * Map a route to the owning page id using the SAME highest-specificity
 * selection the runtime uses to decide which page to render
 * (`AppRuntimeCore.getCurrentPage` → `scorePageSpecificity`, app-runtime-core.ts).
 *
 * Why not `pages.find((p) => matchRoute(p.route, route))`? `matchRoute` treats
 * any `:param` segment as a wildcard and returns a boolean, so `.find` picks the
 * FIRST pattern-overlapping page in manifest order. The runtime instead RENDERS
 * the highest-specificity page (e.g. `/products` beats `/:slug`, `/x/new` beats
 * `/x/:id`). If the tab mapping used first-match while the runtime rendered the
 * specific page, the highlighted tab could disagree with what is on screen
 * (PF-1 / L4-CORR-3). Scoring here keeps tab↔render in lockstep.
 *
 * `scorePageSpecificity` is exported from the renderer's `app-runtime.ts` but
 * NOT from the `@cogo/react-renderer-v2` barrel (only `matchRoute` is), and the
 * runtime's own copy is a private module function — so neither is importable
 * across the package boundary without reaching into internals. We therefore
 * replicate the pure, documented scoring inline: +1 per matching static segment,
 * 0 for a `:param` segment, and a non-match (depth mismatch or a differing
 * static segment) is excluded. Ties resolve to the first page (lowest index),
 * matching the runtime's `b.score - a.score || a.idx - b.idx` sort.
 */
function scorePageSpecificity(pattern: string, path: string): number {
  const patternParts = pattern.split('/')
  const pathParts = path.split('/')
  if (patternParts.length !== pathParts.length) return -1
  let score = 0
  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i]
    if (part.startsWith(':')) continue
    if (part !== pathParts[i]) return -1
    score++
  }
  return score
}

/** Resolve a route to the page id the runtime's `getCurrentPage` would render. */
function resolvePageIdForRoute(
  pages: Page[],
  route: string,
): string | undefined {
  let best: { id: string; score: number; idx: number } | undefined
  pages.forEach((p, idx) => {
    const score = scorePageSpecificity(p.route, route)
    if (score < 0) return
    if (
      !best ||
      score > best.score ||
      (score === best.score && idx < best.idx)
    ) {
      best = { id: p.id, score, idx }
    }
  })
  return best?.id
}

/**
 * Resolve a live `navigate(route)` handle from the runtime exposed via AppV2's
 * `onReady` ctx. The ctx surfaces `{ stateManager, actionEngine }` but not the
 * runtime's `navigate` directly; the engine, however, carries the core-wired
 * platform router whose `pushRoute(route)` runs through `AppRuntimeCore.setRoute`
 * — firing `onNavigate` and re-resolving the current page. Driving navigation
 * through the router (rather than `engine.executeFlow` of a synthetic navigate
 * flow) keeps tab clicks off the ActionFlow trace store.
 *
 * KNOWN COUPLING (L4-CA-02): `engine.platform` is a runtime-internal field
 * (declared `private` in @cogo/runtime-v2's ActionFlowEngine). We read it via a
 * narrow cast because `onReady`'s ctx does not surface the runtime's `navigate`
 * directly. This is feature-detected, not assumed: if a future runtime reshape
 * relocates/renames `platform` (or its `pushRoute`), the surface is absent and
 * we degrade gracefully — design-time tab selection (`setActivePageId`) still
 * works, only live in-place runtime nav is unavailable — while emitting a single
 * loud dev warning so the regression is visible. The "resolveRuntimeNavigate
 * hardening (L4-CA-02)" suite in build-mode-canvas.test.tsx pins this against the
 * REAL ActionFlowEngine shape so a reshape breaks a test LOUDLY here instead of
 * silently no-opping tab nav in production.
 */
export function resolveRuntimeNavigate(
  actionEngine: ActionFlowEngine,
): ((route: string) => void) | null {
  // Narrowly-typed read of a runtime internal (engine.platform.pushRoute). See
  // the KNOWN COUPLING note above — this is the documented coupling point.
  const router = (
    actionEngine as unknown as { platform?: PlatformAdapter | null }
  ).platform
  if (!router || typeof router.pushRoute !== 'function') {
    warnRuntimeNavigateUnavailable()
    return null
  }
  return (route: string) => {
    router.pushRoute(route)
  }
}

// Dev-only one-shot warning: the runtime navigate surface (engine.platform.
// pushRoute) was absent at onReady, so live tab→route nav is degraded to
// design-time tab selection. Fires ONCE per session to avoid log spam.
let warnedRuntimeNavigateUnavailable = false
function warnRuntimeNavigateUnavailable() {
  if (warnedRuntimeNavigateUnavailable) return
  warnedRuntimeNavigateUnavailable = true
  if (import.meta.env.DEV) {
    console.warn(
      '[BuildModeCanvas] runtime navigate handle unavailable ' +
        '(engine.platform.pushRoute missing) — tab clicks fall back to ' +
        'design-time tab selection only; live in-place preview navigation is ' +
        'disabled. This usually means @cogo/runtime-v2 reshaped its platform ' +
        'router surface.',
    )
  }
}

/** @internal test-only — reset the one-shot dev warning latch between cases. */
export function __resetRuntimeNavigateWarning() {
  warnedRuntimeNavigateUnavailable = false
}

/**
 * design-system drift diagnostic (W4-8 D1).
 *
 * The canvas's applied theme tokens are sourced from `projectMeta.
 * design_system_preset_id` (the design system bound to the loaded project
 * row — set alongside `currentProject` in project-slice.ts's `openProject`).
 * `projectPlan.designSystemPresetId` is the explicit row binding persisted
 * alongside the canonical semantic `projectPlan.designSystemId`. The row ids
 * can silently disagree — e.g. a preset was swapped
 * (`preset-binding-strip.tsx`) without re-planning, or a plan was authored
 * before any preset was bound. `resolveDesignSystemDrift` is a pure identity
 * comparison: it returns a drift only when BOTH ids are present AND differ.
 * Either id absent is "undecidable", not drift (a false positive here would
 * be worse than staying silent). This is purely a display diagnostic — it is
 * never used to gate or alter rendering.
 */
function resolveDesignSystemDrift(
  appliedDesignSystemId: string | null | undefined,
  planDesignSystemId: string | null | undefined,
): { appliedId: string; planId: string } | null {
  if (!appliedDesignSystemId || !planDesignSystemId) return null
  if (appliedDesignSystemId === planDesignSystemId) return null
  return { appliedId: appliedDesignSystemId, planId: planDesignSystemId }
}

function DesignSystemDriftDiagnostic({
  appliedId,
  planId,
}: {
  appliedId: string
  planId: string
}) {
  // A normal-flow banner (not `absolute`) — this component is composed as a
  // Fragment sibling of the canvas content (see BuildModeCanvas's return), so
  // it has no guaranteed positioned ancestor to anchor an overlay against.
  // Non-blocking: it never gates or replaces `canvasContent`, only sits above
  // it, and `pointer-events-none` keeps it out of the click-capture path used
  // by A3.4 element selection.
  return (
    <div
      data-testid="design-system-drift-diagnostic"
      role="status"
      className="pointer-events-none z-30 flex shrink-0 items-center justify-center whitespace-nowrap border-b border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200"
    >
      Design system drift — canvas tokens use{' '}
      <code className="mx-1 font-mono">{appliedId}</code>, plan declares{' '}
      <code className="mx-1 font-mono">{planId}</code>
    </div>
  )
}

/**
 * BuildModeCanvas — 5-state machine for cogo-studio Build mode preview.
 *
 * Subscribes to `useStudioStore.dispatchedOutput` and renders one of:
 *   - null            → returns null (parent decides — typically EmptyState branch)
 *   - streaming       → centered shimmer + "Generating: <pageName>"
 *   - compose-preview → centered composing card (yielded before the first
 *                       streaming frame)
 *   - rendered        → page tabs (if pages > 1) + AppV2 mounting the FULL manifest
 *   - shaclFail       → split view: partial AppV2 (left) + orange diagnostics (right)
 *   - miss            → neutral card with English wording from
 *                       getMissReasonCopy + "Try again"/"Start over" button
 *                       (tone-based)
 *
 * AppV2 mount pattern (F7 — preview-fidelity lane):
 *   Canvas owns external page tabs (AppV2 has no internal tab UI). The runtime
 *   mounts the FULL manifest (every page) so runtime navigation can resolve a
 *   sibling route, and `initialRoute` seeds the runtime to the selected tab's
 *   page AT MOUNT. The remount key no longer includes `activePageId`, so a tab
 *   click no longer tears down the runtime (preview state survives across pages).
 *   Instead the live route↔tab link is bidirectional:
 *     • tab onClick → runtime.navigate(page.route)  (drives the long-lived runtime)
 *     • runtime onNavigate(route) → setActivePageId  (tab highlight follows the runtime)
 *   The navigate handle is captured in onReady via the engine's platform router
 *   (pushRoute → core.setRoute → onNavigate + getCurrentPage) — zero renderer change.
 *
 * AppV2 import is lazy, using the shared renderer-loading pattern.
 * Module-level `cachedAppV2` dedupes the import across mounts in the same session.
 */
export function BuildModeCanvas({
  routeSwitcherPortalElement,
  onEditPage,
  hostCapabilityManifestInput,
}: BuildModeCanvasProps = {}) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const out = useStudioStore((s) => s.dispatchedOutput)
  const currentProject = useStudioStore((s) => s.currentProject)
  const previewPageId = useStudioStore((s) => s.currentPageId)
  const appThemeTokens = useStudioStore((s) => s.currentProject?.tokens)
  const clearDispatch = useStudioStore((s) => s.clearDispatch)
  const previewColorScheme = useEditorStore((s) => s.previewColorScheme)
  const themeTokens = useMemo(
    () => toRendererThemeTokens(appThemeTokens),
    [appThemeTokens],
  )

  // design-system drift diagnostic (W4-8 D1) — see resolveDesignSystemDrift.
  const appliedDesignSystemId = useStudioStore(
    (s) => s.projectMeta?.design_system_preset_id,
  )
  const planDesignSystemPresetId = useStudioStore(
    (s) => s.projectPlan?.designSystemPresetId,
  )
  const designSystemDrift = useMemo(
    () =>
      resolveDesignSystemDrift(
        appliedDesignSystemId,
        planDesignSystemPresetId,
      ),
    [appliedDesignSystemId, planDesignSystemPresetId],
  )

  // Host-effect wiring for the four host-effect ActionFlow actions
  // (openModal / invokeCallback / emitEvent / mediaControl) + cropImage. Stable
  // for the lifetime of the canvas so it survives the AppV2 remount on tab
  // switch. See ../host/use-studio-host-services.
  const hostServicesBundle = useStudioHostServices()
  const { hostServices, modalRegistry } = hostServicesBundle
  const runtimePlatformBundle = useMemo(() => createStudioRuntimePlatform(), [])

  const [AppV2, setAppV2] = useState<AppV2Component | null>(
    () => cachedAppV2 ?? null,
  )
  const [cropImageImplementation, setCropImageImplementation] =
    useState<StudioCropImageImplementation | undefined>(
      () => cachedCropImageWeb,
    )

  // Lazy AppV2 import — deduped via module-level cache.
  useEffect(() => {
    if (cachedAppV2) return
    let mounted = true
    import('@cogo/react-renderer-v2')
      .then((m) => {
        cachedAppV2 = (m as { AppV2: AppV2Component }).AppV2
        const cropImageDescriptor = Object.getOwnPropertyDescriptor(
          m,
          'cropImageWeb',
        )
        if (typeof cropImageDescriptor?.value === 'function') {
          cachedCropImageWeb = cropImageDescriptor.value as StudioCropImageImplementation
        }
        if (mounted) setAppV2(() => cachedAppV2!)
        if (mounted) setCropImageImplementation(() => cachedCropImageWeb)
      })
      .catch((err) => {
        console.warn('[BuildModeCanvas] AppV2 dynamic import failed', err)
      })
    return () => {
      mounted = false
    }
  }, [])

  const currentProjectPreview = currentProject ? (
    <RenderedView
      manifest={currentProject.manifest}
      blocks={currentProject.blocks}
      AppV2={AppV2}
      hostServices={hostServices}
      modalRegistry={modalRegistry}
      hostServicesBundle={hostServicesBundle}
      runtimePlatformBundle={runtimePlatformBundle}
      hostCapabilityManifestInput={hostCapabilityManifestInput}
      cropImageImplementation={cropImageImplementation}
      themeTokens={themeTokens}
      colorScheme={previewColorScheme}
      showPageNavigation={false}
      requireAttachedPageBlock={false}
      externalPageId={previewPageId}
      routeSwitcherPortalElement={routeSwitcherPortalElement}
      onEditPage={onEditPage}
    />
  ) : null

  // canvasContent resolves the same 5-arm switch as before. It is wrapped
  // in an IIFE (rather than returned directly from the component) so the
  // design-system drift diagnostic badge (W4-8 D1, purely additive/
  // non-blocking) can be composed alongside it below — the switch itself is
  // untouched.
  const canvasContent = (() => {
    if (!out) {
      return currentProjectPreview
    }

    switch (out.kind) {
      case 'streaming':
        return currentProjectPreview ? (
          <StreamingPreviewShell pages={out.pages}>
            {currentProjectPreview}
          </StreamingPreviewShell>
        ) : (
          <StreamingSkeleton pages={out.pages} />
        )
      // compose-preview is yielded before the first streaming frame; without
      // a case the canvas went blank (EmptyState is hidden once output is
      // non-null) while the compose card sat only in the chat rail.
      case 'compose-preview':
        return (
          <div
            data-testid="compose-preview-canvas"
            className="flex h-full flex-col items-center justify-center gap-2 text-center"
          >
            <p className="text-sm font-medium text-foreground">{copy.composePreview.composing}</p>
            <p className="text-xs text-muted-foreground">
              {copy.composePreview.summary(out.plan.readyCandidateCount, out.plan.candidateCount, out.plan.pageCount)}
            </p>
          </div>
        )
      case 'rendered': {
        // The three-marks provenance card (§4): derived only from signals
        // the pipeline recorded — backend generation provenance on this
        // output plus the project's birth-written intake decisions. Both
        // absent → no card (never an empty overclaim).
        const projectSettings = (currentProject as { settings?: unknown } | null)
          ?.settings
        const intakeDecisions = readIntakeDecisionsFromSettings(projectSettings)
        const provenanceView = deriveGenerationProvenance({
          provenance: out.provenance ?? null,
          intakeDecisions,
        })
        // §7-1 — M7 on screen: declared requirements checked structurally
        // against THIS output's blocks. §7-2 — spec-field provenance seat,
        // silent until Task B7 records the data.
        const realization = measureRequirementRealization({
          blocks: out.blocks,
          intakeDecisions,
        })
        const specProvenance = readSpecFieldProvenanceFromSettings(projectSettings)
        return (
          <div className="flex h-full flex-col">
            {hasProvenanceContent(provenanceView) ||
            realization ||
            specProvenance ? (
              <GenerationProvenanceCard
                view={provenanceView}
                realization={realization}
                specProvenance={specProvenance}
              />
            ) : null}
            <RenderedView
            manifest={out.manifest}
            blocks={out.blocks}
            AppV2={AppV2}
            renderIdentity={out.shapeSha}
            hostServices={hostServices}
            modalRegistry={modalRegistry}
            hostServicesBundle={hostServicesBundle}
            runtimePlatformBundle={runtimePlatformBundle}
            hostCapabilityManifestInput={hostCapabilityManifestInput}
            cropImageImplementation={cropImageImplementation}
            themeTokens={themeTokens}
            colorScheme={previewColorScheme}
            showPageNavigation
            requireAttachedPageBlock
            routeSwitcherPortalElement={routeSwitcherPortalElement}
            onEditPage={onEditPage}
          />
          </div>
        )
      }
      case 'shaclFail':
        return (
          <ShaclFailView
            diagnostics={out.diagnostics}
            failedPageId={out.failedPageId}
            partialResult={out.partialResult}
            AppV2={AppV2}
            hostServices={hostServices}
            modalRegistry={modalRegistry}
            runtimePlatformBundle={runtimePlatformBundle}
            themeTokens={themeTokens}
            colorScheme={previewColorScheme}
          />
        )
      case 'miss':
        return (
          <MissView
            reason={out.reason}
            details={out.details}
            onRetry={clearDispatch}
          />
        )
    }
  })()

  return (
    <>
      {designSystemDrift && (
        <DesignSystemDriftDiagnostic
          appliedId={designSystemDrift.appliedId}
          planId={designSystemDrift.planId}
        />
      )}
      {canvasContent}
    </>
  )
}

// ─── Streaming shimmer ────────────────────────────────────────────────────────

function StreamingSkeleton({ pages }: { pages: PageProgress[] }) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const inProgress = pages.find((p) => p.status === 'in-progress')
  const currentName = inProgress?.name ?? pages[0]?.name ?? '...'
  const channelDispatchStatus = useStudioStore((s) => s.channelDispatchStatus)
  const modeLabel = channelDispatchStatus.mode === 'real' ? copy.real : copy.mock
  const statusText = channelDispatchStatus.message ?? copy.draftingStartingPoint
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground"
      data-testid="canvas-streaming"
    >
      <div className="h-2 w-40 overflow-hidden rounded-full bg-muted">
        <div className="h-full w-1/3 animate-pulse bg-sky-500" />
      </div>
      <div className="text-sm">{copy.generating(currentName)}</div>
      <div
        data-testid="generation-contract-status-active"
        className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs"
      >
        <span className="font-mono text-[10px] uppercase tracking-wide text-foreground">
          {modeLabel}
        </span>
        <span>{statusText}</span>
      </div>
      <button
        type="button"
        onClick={abortActiveChannelDispatch}
        aria-label={copy.cancelGeneration}
        className="inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
      >
        <Square size={13} aria-hidden />
        {copy.cancel}
      </button>
    </div>
  )
}

function StreamingPreviewShell({ pages, children }: { pages: PageProgress[]; children: ReactNode }) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const inProgress = pages.find((p) => p.status === 'in-progress')
  const currentName = inProgress?.name ?? pages[0]?.name ?? '...'
  const channelDispatchStatus = useStudioStore((s) => s.channelDispatchStatus)
  const modeLabel = channelDispatchStatus.mode === 'real' ? copy.real : copy.mock
  const statusText = channelDispatchStatus.message ?? copy.applyingRequest

  return (
    <div
      className="relative h-full min-h-0"
      data-testid="canvas-streaming-preview"
    >
      {children}
      <div className="pointer-events-none absolute inset-x-4 bottom-4 z-20 flex justify-center sm:justify-start">
        <div className="pointer-events-auto flex max-w-full items-center gap-2 rounded-lg border border-border bg-card/95 px-3 py-2 text-xs text-muted-foreground shadow-lg backdrop-blur">
          <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-sky-500" aria-hidden />
          <span className="min-w-0 truncate">{copy.updating(currentName, modeLabel, statusText)}</span>
          <button
            type="button"
            onClick={abortActiveChannelDispatch}
            aria-label={copy.cancelGeneration}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-[11px] font-medium text-foreground hover:bg-accent"
          >
            <Square size={12} aria-hidden />
            {copy.cancel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Preview route switcher ──────────────────────────────────────────────────

function getPageTitle(page: Page): string {
  const record = page as Page & { title?: string }
  return page.name?.trim() || record.title?.trim() || page.id
}

function getPageRoute(page: Page): string {
  return page.route?.trim() || '/'
}

function getBlockRoot(block: BlockDef | undefined): CogoElement | undefined {
  const candidate = block as
    | {
        ui?: { root?: CogoElement }
        root?: CogoElement
      }
    | undefined
  return candidate?.ui?.root ?? candidate?.root
}

function resolveOwningBlockName(
  blocks: Record<string, BlockDef>,
  elementId: string,
  activePage?: Page,
): string | undefined {
  const visited = new Set<string>()
  const findInBlock = (blockName: string): string | undefined => {
    if (visited.has(blockName)) return undefined
    visited.add(blockName)
    const root = getBlockRoot(blocks[blockName])
    return root && findElementInTree(root, elementId) ? blockName : undefined
  }

  for (const blockName of activePage?.blocks ?? []) {
    const match = findInBlock(blockName)
    if (match) return match
  }

  for (const blockName of Object.keys(blocks)) {
    const match = findInBlock(blockName)
    if (match) return match
  }

  return undefined
}

function PreviewRouteSwitcher({
  pages,
  activePageId,
  onSelectPage,
  onEditPage,
  compact,
}: {
  pages: Page[]
  activePageId: string
  onSelectPage: (page: Page) => void
  onEditPage?: (pageId: string) => void
  compact: boolean
}) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const activePage = pages.find((page) => page.id === activePageId) ?? pages[0]
  const normalizedQuery = query.trim().toLowerCase()
  const filteredPages = useMemo(() => {
    if (!normalizedQuery) return pages
    return pages.filter((page) => {
      const haystack =
        `${page.id} ${getPageTitle(page)} ${getPageRoute(page)}`.toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [normalizedQuery, pages])

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const currentTitle = getPageTitle(activePage)
  const currentRoute = getPageRoute(activePage)

  return (
    <div
      ref={rootRef}
      data-testid="preview-route-switcher"
      data-active-page-id={activePage.id}
      className={cn(
        'relative flex min-w-0 items-center justify-between gap-2 rounded-lg border border-border bg-background px-2 py-1 shadow-sm',
        compact ? 'w-full' : 'mx-3 my-2',
      )}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Route className="h-3.5 w-3.5" aria-hidden />
        </div>
        <button
          type="button"
          data-testid="preview-route-current"
          data-active-page-id={activePage.id}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="flex h-8 min-w-0 max-w-[28rem] items-center gap-2 rounded-md px-2 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="shrink-0 font-medium text-muted-foreground">{copy.screen}</span>
          <span className="truncate font-semibold text-foreground">{currentTitle}</span>
          <span className="max-w-[12rem] truncate text-[11px] text-muted-foreground">
            {pages.length > 1
              ? copy.screenCount(pages.findIndex((page) => page.id === activePage.id) + 1, pages.length)
              : currentRoute}
          </span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </div>

      <button
        type="button"
        data-testid="preview-edit-page"
        disabled={!activePage.id || !onEditPage}
        onClick={() => activePage.id && onEditPage?.(activePage.id)}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-45"
      >
        <FilePenLine className="h-3.5 w-3.5" aria-hidden />
        {copy.editScreen}
      </button>

      {open && (
        <div
          data-testid="preview-route-menu"
          role="menu"
          className={cn(
            'absolute left-0 top-[calc(100%+0.375rem)] z-50 rounded-lg border border-border bg-popover p-2 shadow-lg',
            compact ? 'w-full' : 'w-[min(32rem,calc(100vw-2rem))]',
          )}
        >
          <label className="flex h-8 items-center gap-2 rounded-md border border-border bg-background px-2 text-xs text-muted-foreground">
            <Search className="h-3.5 w-3.5" aria-hidden />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={copy.searchScreens}
              className="min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </label>
          <div className="mt-2 max-h-72 overflow-y-auto pr-1">
            {filteredPages.length > 0 ? (
              filteredPages.map((page, index) => {
                const selected = page.id === activePage.id
                return (
                  <button
                    key={`${page.id}:${index}`}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    data-testid={`preview-route-option-${page.id}`}
                    onClick={() => {
                      onSelectPage(page)
                      setOpen(false)
                      setQuery('')
                    }}
                    className={cn(
                      'flex w-full min-w-0 items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-xs transition-colors',
                      selected
                        ? 'bg-foreground text-background'
                        : 'text-foreground hover:bg-muted',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {getPageTitle(page)}
                      </span>
                      <span
                        className={cn(
                          'block truncate font-mono text-[11px]',
                          selected
                            ? 'text-background/75'
                            : 'text-muted-foreground',
                        )}
                      >
                        {getPageRoute(page)}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'max-w-28 shrink-0 truncate rounded-sm px-1.5 py-0.5 font-mono text-[10px]',
                        selected
                          ? 'bg-background/15'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {page.id}
                    </span>
                  </button>
                )
              })
            ) : (
              <div className="px-2 py-6 text-center text-xs text-muted-foreground">{copy.noScreensFound}</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Rendered with page tabs + AppV2 single-page swap ────────────────────────

function RenderedView({
  manifest,
  blocks,
  AppV2,
  renderIdentity,
  hostServices,
  modalRegistry,
  hostServicesBundle,
  runtimePlatformBundle,
  hostCapabilityManifestInput,
  cropImageImplementation,
  themeTokens,
  colorScheme,
  showPageNavigation,
  requireAttachedPageBlock,
  externalPageId,
  routeSwitcherPortalElement,
  onEditPage,
}: {
  manifest: AppManifest
  blocks: Record<string, BlockDef>
  AppV2: AppV2Component | null
  renderIdentity?: string
  hostServices: HostServices
  modalRegistry: ModalComponentRegistry
  hostServicesBundle: StudioHostServices
  runtimePlatformBundle: StudioRuntimePlatformBundle | undefined
  hostCapabilityManifestInput?: StudioHostCapabilityManifestEvidenceInput
  cropImageImplementation?: StudioCropImageImplementation
  themeTokens?: AppV2Props['themeTokens']
  colorScheme: NonNullable<AppV2Props['colorScheme']>
  showPageNavigation: boolean
  requireAttachedPageBlock: boolean
  externalPageId?: string | null
  routeSwitcherPortalElement?: HTMLElement | null
  onEditPage?: (pageId: string) => void
}) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const pages = useMemo<Page[]>(() => manifest.pages ?? [], [manifest.pages])
  const hasAttachedPageBlock = useMemo(
    () =>
      pages.some((page) =>
        (page.blocks ?? []).some((blockName) =>
          Boolean(blocks[blockName]),
        ),
      ),
    [blocks, pages],
  )
  const previewFallback = requireAttachedPageBlock && !hasAttachedPageBlock
    ? {
        title: copy.previewNotReadyTitle,
        description: copy.previewNotReadyDescription,
        testId: "generated-app-not-ready",
      }
    : !AppV2
      ? {
          title: copy.previewUnavailableTitle,
          description: copy.previewUnavailableDescription,
          testId: "generated-app-unavailable",
        }
      : {
          title: copy.previewPreparingTitle,
          description: copy.previewPreparingDescription,
          testId: "generated-app-preparing",
        }
  const setCurrentPageId = useStudioStore((s) => s.setCurrentPageId)
  const initialPageId =
    externalPageId && pages.some((page) => page.id === externalPageId)
      ? externalPageId
      : (pages[0]?.id ?? '')
  const [activePageId, setActivePageId] = useState<string>(initialPageId)
  const setChatRailChip = useStudioStore((s) => s.setChatRailChip)
  const thumbnailProjectId = useStudioStore((s) => s.currentProjectId)
  const lastEventHintRef = useRef<PreviewEventHint | null>(null)
  const renderedRootRef = useRef<HTMLDivElement>(null)
  const appDesignRootRef = useRef<HTMLDivElement>(null)
  // Live navigate handle into the long-lived runtime, captured in onReady (see
  // handleRuntimeReady). Drives a page change without remounting — the runtime
  // survives tab switches, so this is the ONLY way a tab click changes the
  // preview route now that activePageId is out of the remount key.
  const runtimeNavigateRef = useRef<((route: string) => void) | null>(null)

  // The route the runtime should open on AT MOUNT — the first page of the
  // current generation (which is also the active tab at mount, since the reset
  // effect below sets activePageId to initialPageId on every new output).
  // Derived from initialPageId, NOT the live activePageId: a remount only ever
  // happens on a new generated output, at which point the active tab has reset
  // to the first page. Post-mount tab clicks drive the live runtime via
  // runtimeNavigateRef (the remount key excludes activePageId, so no remount),
  // so seeding from activePageId here would just stale-seed the next remount.
  const initialRoute = useMemo(
    () => pages.find((p) => p.id === initialPageId)?.route ?? pages[0]?.route,
    [pages, initialPageId],
  )

  // Remount key — generation identity only (renderIdentity / appId / page+block
  // signatures). activePageId is DELIBERATELY excluded: a tab switch must NOT
  // remount AppV2 (that would wipe all preview state). The only remounts are
  // genuine content changes (a new generated output → different renderIdentity),
  // which is the intended reset.
  const appRemountKey = useMemo(() => {
    const pageSignature = pages
      .map((page) => `${page.id}:${page.blocks?.join(',') ?? ''}`)
      .join('|')
    const blockSignature = buildBlocksContentSignature(blocks)
    return `${renderIdentity ?? manifest.appId ?? manifest.name ?? 'app'}:${pageSignature}:${blockSignature}`
  }, [blocks, manifest.appId, manifest.name, pages, renderIdentity])

  // A3.4 — click feedback (master spec §8.1 step 1+2)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null,
  )

  // Resync the active tab to the runtime's seeded initial page (the first page)
  // whenever the runtime REMOUNTS — keyed on appRemountKey, NOT initialPageId.
  // A same-first-page regeneration (REG-1) keeps initialPageId stable ('login'
  // stays 'login') but changes the block/render signature → AppV2 remounts and
  // re-seeds initialRoute to the first page. Keying only on initialPageId would
  // miss that remount, stranding the highlight on a non-first tab the user had
  // selected while the runtime renders the first page. Keying on appRemountKey
  // fires on every genuine remount (and the initial mount), keeping the tab in
  // lockstep with what the runtime opens. (A bare tab switch does NOT change
  // appRemountKey — activePageId is excluded from it — so this never fights the
  // live route↔tab sync.)
  useEffect(() => {
    setActivePageId(initialPageId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appRemountKey])

  // Map a runtime route back to the owning page tab using the SAME
  // highest-specificity selection the runtime uses to decide which page to
  // render (resolvePageIdForRoute → scorePageSpecificity, mirroring
  // AppRuntimeCore.getCurrentPage). First-match via matchRoute would highlight
  // the wrong tab when routes overlap (a `:param` route ordered before a
  // concrete one) because matchRoute treats `:param` as a wildcard while the
  // runtime renders the concrete page (PF-1 / L4-CORR-3). Runtime → tab
  // highlight. Guards the feedback loop: only updates state when the id actually
  // changes, and NEVER calls runtimeNavigate from here (that is driven solely by
  // the user tab onClick).
  const handleRuntimeNavigate = useCallback(
    (route: string) => {
      const matched = resolvePageIdForRoute(pages, route)
      if (matched) {
        const matchedPage = pages.find((page) => page.id === matched)
        setActivePageId((prev) => (prev === matched ? prev : matched))
        setCurrentPageId(matched)
        useEditorStore.setState({
          activePageId: matched,
          selectedPageId: null,
          selectedIds: [],
          selectedBlockName: null,
          selectionTier: 'page',
        })
        const studio = useStudioStore.getState()
        studio.setChatContext({
          ...studio.chatContext,
          pageId: matched,
          pageName: matchedPage ? getPageTitle(matchedPage) : matched,
          selectionSurface: 'preview',
          selectedElementId: undefined,
          selectedElementLabel: undefined,
          selectedElementType: undefined,
          selectedBlockName: undefined,
          selectedBlockLabel: undefined,
          selectedBlockType: undefined,
          selectedPropertyKey: undefined,
        })
      }
    },
    [pages, setCurrentPageId],
  )

  // Tab click → drive the live runtime (not just local tab state). Updating
  // activePageId keeps the studio tab picker in sync immediately; navigating the
  // runtime switches the preview page in-place (the runtime's onNavigate will
  // also fire handleRuntimeNavigate, a no-op re-set on the same id).
  //
  // Active-tab guard (L4-CORR-4): if the clicked page is already active, do NOT
  // re-drive runtimeNavigate. core.setRoute has no unchanged-route short-circuit
  // — it always runs lifecycle.resetForRoute() + runInitFlows(), which would
  // wipe the page's accumulated preview state (the opposite of the F7 goal).
  // setActivePageId(page.id) is a no-op on the same id, so re-clicking the active
  // tab becomes fully inert.
  const handleTabClickRef = useRef<(page: Page) => void>(() => {})
  handleTabClickRef.current = (page: Page) => {
    if (page.id === activePageId) return
    setActivePageId(page.id)
    runtimeNavigateRef.current?.(page.route)
  }
  const handleTabClick = useCallback((page: Page) => {
    handleTabClickRef.current(page)
  }, [])

  useEffect(() => {
    if (!externalPageId) return
    const page = pages.find((candidate) => candidate.id === externalPageId)
    if (!page) return
    setActivePageId((prev) => (prev === page.id ? prev : page.id))
    runtimeNavigateRef.current?.(page.route)
  }, [externalPageId, pages])

  const routeSwitcher =
    (showPageNavigation || routeSwitcherPortalElement) && pages.length > 0 ? (
      <PreviewRouteSwitcher
        pages={pages}
        activePageId={activePageId}
        onSelectPage={handleTabClick}
        onEditPage={onEditPage}
        compact={Boolean(routeSwitcherPortalElement)}
      />
    ) : null

  const renderPackBytes = useMemo(
    () =>
      buildPreviewRenderPackBytes(blocks as unknown as Record<string, unknown>),
    [blocks],
  )
  const { sandbox, ready: appSandboxReady } = useAppDesignSandbox({
    appId: manifest.appId ?? manifest.name ?? 'generated-app',
    tokens: themeTokens,
    colorScheme,
    containerRef: appDesignRootRef,
    apiBaseUrl: getStudioRuntimeApiBase(),
  })
  const appRenderRoot = appSandboxReady
    ? (sandbox?.modules.dom.renderRoot ?? null)
    : null

  // This canvas is the only surface that renders a DRAFT project's own block
  // bundle, so it is also the only thing a draft thumbnail can be taken from.
  useProjectThumbnailAutoCapture(
    appDesignRootRef,
    thumbnailProjectId,
    Boolean(AppV2 && appSandboxReady && appRenderRoot),
  )

  useEffect(() => {
    if (!AppV2 || !appSandboxReady || !appRenderRoot) return
    const transaction = useStudioStore.getState().agenticUxTransaction
    if (
      transaction?.status !== 'running' ||
      transaction.phase !== 'spec-mutating'
    )
      return
    useStudioStore.getState().advanceAgenticUxTransaction(transaction.id, {
      phase: 'render-committed',
    })
  }, [AppV2, appRemountKey, appRenderRoot, appSandboxReady])

  // A3.4 — element click capture → chatRailChip
  // The renderer provides element identity in the DOM, but block wrappers can be
  // coarser than the Cogo model after app-level preview composition. Resolve the
  // owner block from the Coogo block tree first, then fall back to the DOM wrapper
  // for legacy/synthetic surfaces.
  const handleElementClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    // Only capture refinement chips and show refinement outlines in build mode
    const studioMode = useStudioStore.getState().studioMode
    if (studioMode !== 'build') return

    const target = (e.target as HTMLElement).closest(
      '[data-cogo-element-id]',
    ) as HTMLElement | null
    if (!target) return
    const element_id = target.getAttribute('data-cogo-element-id')
    const element_type = target.getAttribute('data-cogo-type')
    const blockWrapper = target.closest(
      '[data-cogo-block-id]',
    ) as HTMLElement | null
    const domBlockId = blockWrapper?.getAttribute('data-cogo-block-id')
    const activePage = pages.find((page) => page.id === activePageId)
    const block_id = element_id
      ? (resolveOwningBlockName(blocks, element_id, activePage) ?? domBlockId)
      : domBlockId
    if (!element_id || !element_type || !block_id) {
      if (import.meta.env.DEV) {
        console.warn('[chat-rail] click missing identity:', {
          element_id,
          element_type,
          block_id,
        })
      }
      return
    }

    // A3.4 click feedback (master spec §8.1 step 1+2)
    target.classList.add('cogo-chip-target')
    setTimeout(
      () => target.classList.remove('cogo-chip-target'),
      CHIP_OUTLINE_HOLD_MS,
    )

    const rect = target.getBoundingClientRect()
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 })
    setTimeout(() => setTooltipPos(null), CHIP_TOOLTIP_HOLD_MS)

    const trimmed = (target.textContent ?? '').trim().slice(0, 30)
    const label = trimmed || element_type
    setChatRailChip({ element_id, element_type, block_id, label })
    const currentContext = useStudioStore.getState().chatContext
    useStudioStore.getState().setChatContext({
      ...currentContext,
      pageId: activePage?.id ?? activePageId,
      pageName: activePage ? getPageTitle(activePage) : currentContext.pageName,
      selectionSurface: 'preview',
      selectedElementId: element_id,
      selectedElementLabel: label,
      selectedBlockName: block_id,
      selectedElementType: element_type,
    })
  }

  const recordRuntimeEvent = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>) => {
      const hint = getPreviewEventHint(event.nativeEvent)
      if (hint) lastEventHintRef.current = hint
    },
    [],
  )

  const handleRuntimeReady = useCallback<NonNullable<AppV2Props['onReady']>>(
    ({ stateManager, actionEngine }) => {
      installActionFlowTraceCapture({
        stateManager,
        actionEngine,
        getEventHint: () => lastEventHintRef.current,
        getRendererProjection: () =>
          collectRendererProjection(appRenderRoot ?? renderedRootRef.current),
        getProductionBinding: () => readStudioTraceProjectBinding(
          useStudioStore.getState(),
        ),
        onTrace: (trace) => {
          const studioStore = useStudioStore.getState()
          usePreviewTestingStore.getState().addActionFlowTrace(trace)
          studioStore.applyActionFlowBrowserTraceEvidence(trace)
          studioStore.replacePageProgress(null)
        },
      })
      // Capture a live navigate handle so a tab click can change the preview
      // page WITHOUT remounting the runtime (which would wipe preview state).
      // The engine carries the core-wired platform router whose pushRoute →
      // core.setRoute fires onNavigate + getCurrentPage. Going through the
      // router (not engine.executeFlow) avoids polluting the trace store with a
      // spurious navigate trace on every tab click. onReady's ctx does not
      // expose navigate directly, so we read it off the engine's router; the
      // guarded resolver no-ops if the surface is ever absent.
      runtimeNavigateRef.current = resolveRuntimeNavigate(actionEngine)
    },
    [appRenderRoot],
  )

  const directAppV2EffectBundle = useMemo(
    () =>
      createStudioDirectAppV2EffectBundle({
        onNavigate: handleRuntimeNavigate,
        onToast: showStudioRuntimeToast,
        onReady: handleRuntimeReady,
        hostServices,
        cropImageImplementation,
      }),
    [
      cropImageImplementation,
      handleRuntimeNavigate,
      handleRuntimeReady,
      hostServices,
    ],
  )
  const hostCapabilityManifest = useMemo(
    () =>
      hostCapabilityManifestInput && runtimePlatformBundle
        ? materializeStudioHostCapabilityManifest({
            ...hostCapabilityManifestInput,
            runtimePlatformBundle,
            hostServicesBundle,
            directAppV2EffectBundle,
          })
        : undefined,
    [
      directAppV2EffectBundle,
      hostCapabilityManifestInput,
      hostServicesBundle,
      runtimePlatformBundle,
    ],
  )

  return (
    <div
      ref={renderedRootRef}
      className="relative flex h-full min-h-0 flex-col"
      data-testid="canvas-rendered"
      data-host-capability-manifest-hash={hostCapabilityManifest?.contentHash}
      data-preview-color-scheme={colorScheme}
      onClickCapture={recordRuntimeEvent}
      onChangeCapture={recordRuntimeEvent}
      onInputCapture={recordRuntimeEvent}
      onClick={(event) => {
        handleElementClick(event)
      }}
    >
      {routeSwitcherPortalElement && routeSwitcher
        ? createPortal(routeSwitcher, routeSwitcherPortalElement)
        : routeSwitcher}
      <div
        className="min-h-0 flex-1 overflow-hidden"
        data-testid="generated-app-rendered"
        role="region"
        aria-label={copy.generatedApp}
        data-cogo-studio-app-preview-boundary="true"
      >
        <div
          ref={appDesignRootRef}
          className="min-h-full"
          data-cogo-app-design-sandbox={appSandboxReady ? 'ready' : 'mounting'}
          data-cogo-app-design-root="true"
        >
          {AppV2 &&
          appSandboxReady &&
          sandbox &&
          appRenderRoot &&
          (!requireAttachedPageBlock || hasAttachedPageBlock) ? (
            createPortal(
              <AppV2
                key={appRemountKey}
                manifest={manifest}
                initialRoute={initialRoute}
                onNavigate={directAppV2EffectBundle.appV2Props.onNavigate}
                blocks={blocks}
                renderPackBytes={renderPackBytes}
                onReady={directAppV2EffectBundle.appV2Props.onReady}
                hostServices={hostServices}
                modalRegistry={modalRegistry}
                themeTokens={themeTokens}
                colorScheme={colorScheme}
                platform={runtimePlatformBundle?.platform}
                sandbox={sandbox}
                requireDesignSandbox
                islands={STUDIO_PREVIEW_ISLANDS}
                onToast={directAppV2EffectBundle.appV2Props.onToast}
              />,
              appRenderRoot,
            )
          ) : (
            <div
              className="flex h-full min-h-[18rem] items-center justify-center p-6"
              data-testid={previewFallback.testId}
              role="status"
            >
              <div className="max-w-md rounded-xl border border-dashed border-border bg-muted/40 p-5 text-center">
                <p className="text-sm font-semibold text-foreground">
                  {previewFallback.title}
                </p>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  {previewFallback.description}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {tooltipPos && (
        <div
          data-testid="chip-tooltip"
          className="fixed pointer-events-none px-2 py-1 bg-foreground text-background text-xs rounded shadow z-50 -translate-x-1/2 -translate-y-full"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
        >
          {copy.selectedForChat}
        </div>
      )}
    </div>
  )
}

function buildBlocksContentSignature(blocks: Record<string, BlockDef>): string {
  return Object.keys(blocks)
    .sort()
    .map(
      (name) =>
        `${name}:${hashUiNode((blocks[name] as any)?.ui?.root ?? (blocks[name] as any)?.root)}`,
    )
    .join('|')
}

function hashUiNode(node: any): string {
  let hash = 2166136261
  let count = 0
  const visit = (value: any) => {
    if (!value || typeof value !== 'object') return
    count += 1
    hash = hashText(hash, String(value.id ?? ''))
    hash = hashText(hash, String(value.type ?? ''))
    hash = hashText(hash, String(value.text ?? value.value ?? ''))
    hash = hashText(hash, String(value.className ?? ''))
    const children = Array.isArray(value.children) ? value.children : []
    for (const child of children) visit(child)
  }
  visit(node)
  return `${count}:${(hash >>> 0).toString(36)}`
}

function hashText(seed: number, text: string): number {
  let hash = seed
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash
}

// ─── ShaclFail split view (partial AppV2 left, diagnostics right) ────────────

function ShaclFailView({
  diagnostics,
  failedPageId,
  partialResult,
  AppV2,
  hostServices,
  modalRegistry,
  runtimePlatformBundle,
  themeTokens,
  colorScheme,
}: {
  diagnostics: string[]
  failedPageId?: string
  partialResult?: { manifest: AppManifest; blocks: Record<string, BlockDef> }
  AppV2: AppV2Component | null
  hostServices: HostServices
  modalRegistry: ModalComponentRegistry
  runtimePlatformBundle: StudioRuntimePlatformBundle | undefined
  themeTokens?: AppV2Props['themeTokens']
  colorScheme: NonNullable<AppV2Props['colorScheme']>
}) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const partialAppDesignRootRef = useRef<HTMLDivElement>(null)
  const renderPackBytes = useMemo(
    () =>
      partialResult
        ? buildPreviewRenderPackBytes(
            partialResult.blocks as unknown as Record<string, unknown>,
          )
        : undefined,
    [partialResult],
  )
  const { sandbox, ready: appSandboxReady } = useAppDesignSandbox({
    appId:
      partialResult?.manifest.appId ??
      partialResult?.manifest.name ??
      'partial-generated-app',
    tokens: themeTokens,
    colorScheme,
    containerRef: partialAppDesignRootRef,
    apiBaseUrl: getStudioRuntimeApiBase(),
  })
  const appRenderRoot = appSandboxReady
    ? (sandbox?.modules.dom.renderRoot ?? null)
    : null

  return (
    <div
      className="grid h-full grid-cols-2 gap-3 p-3"
      data-testid="canvas-shacl-fail"
    >
      <div
        className="overflow-auto rounded-md border border-border bg-muted/40"
        data-cogo-studio-app-preview-boundary="true"
      >
        <div
          ref={partialAppDesignRootRef}
          className="min-h-full"
          data-cogo-app-design-sandbox={appSandboxReady ? 'ready' : 'mounting'}
          data-cogo-app-design-root="true"
        >
          {partialResult?.manifest &&
          AppV2 &&
          appSandboxReady &&
          sandbox &&
          appRenderRoot ? (
            createPortal(
              <AppV2
                manifest={partialResult.manifest}
                blocks={partialResult.blocks}
                renderPackBytes={renderPackBytes}
                hostServices={hostServices}
                modalRegistry={modalRegistry}
                themeTokens={themeTokens}
                colorScheme={colorScheme}
                platform={runtimePlatformBundle?.platform}
                sandbox={sandbox}
                requireDesignSandbox
                islands={STUDIO_PREVIEW_ISLANDS}
                onToast={showStudioRuntimeToast}
              />,
              appRenderRoot,
            )
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {copy.noPartialResult}
            </div>
          )}
        </div>
      </div>
      <div className="overflow-auto rounded-md border border-orange-300 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950/40">
        <div className="font-medium text-orange-900 dark:text-orange-200">{copy.checksFailed(failedPageId)}</div>
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-orange-800 dark:text-orange-300">
          {diagnostics.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ─── Miss view with retry → clearDispatch ────────────────────────────────────

function MissView({ reason, details, onRetry }: { reason: ChannelMissReason; details?: string; onRetry: () => void }) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const { tone } = getMissReasonCopy(reason)
  const title = copy.missReason[reason]
  return (
    <div
      className="flex h-full items-center justify-center p-4"
      data-testid="canvas-miss"
    >
      <div className="max-w-md rounded-md border border-destructive/30 bg-destructive/5 p-4">
        <div className="font-medium text-foreground">
          <span>{title}</span>
        </div>
        {details && (
          <div className="mt-1 text-xs text-muted-foreground">{details}</div>
        )}
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-md border border-border bg-card px-3 py-1 text-sm text-foreground hover:bg-accent"
        >
          {tone === 'transient' ? copy.tryAgain : copy.startOver}
        </button>
      </div>
    </div>
  )
}

// Re-exported helpers for downstream channel-rebridge code (optional). The four
// view components are scoped to this module — the only public surface is
// BuildModeCanvas (subscribes to store, no props).

// Re-export ChannelOutput type so consumers can import { type } alongside the
// component without reaching into _shared/channel-types directly.
export type { ChannelOutput }
